import { useEffect, useState } from 'react'

import boxConfigurations from '@data/boxConfigurations.json'

import type { CustomizationSelectionType } from '@lib/types/customization'

import { filterBonusGroups } from '@lib/customization/bonus-items'
import styles from './styles/CustomizationModal.module.scss'

type CustomizationActionBarProps = {
	closeModal: () => void
	currentStep: number
	setStep: (step: number) => void
	currentSelection: CustomizationSelectionType | null
	submitSelections: () => void
	customizationType: 'NMC' | 'RMC'
	subscriptionProductSKU: string
}

export default function CustomizationActionBar({
	closeModal,
	currentStep,
	setStep,
	currentSelection,
	submitSelections,
	customizationType,
	subscriptionProductSKU
}: CustomizationActionBarProps) {
	const [showErrorMessage, setShowErrorMessage] = useState(false)

	useEffect(() => {
		const modalElement = document.getElementById('customizationModal')
		if (modalElement) {
			modalElement.scrollTo(0, 0)
		}
	}, [currentStep])

	useEffect(() => {
		// Reset error message visibility when moving to a new step
		setShowErrorMessage(false)

		// Show error message after a short delay if button is disabled
		const timer = setTimeout(() => {
			if (currentSelection?.selections.length !== currentSelection?.maxItems) {
				setShowErrorMessage(true)
			}
		}, 1500) // Add delay so user interacts prior to message showing

		return () => clearTimeout(timer)
	}, [currentSelection])

	const boxConfiguration = boxConfigurations.find((config) =>
		config.product_skus.some((sku) => sku.sku === subscriptionProductSKU)
	)

	if (!boxConfiguration) {
		console.error(`No box configuration found for SKU: ${subscriptionProductSKU}`)
		return <div>Error: No box configuration found</div>
	}

	const allCustomizationSteps =
		customizationType === 'NMC'
			? boxConfiguration.nmc.customization_groups
			: boxConfiguration.rmc.customization_groups
	const customizationSteps = filterBonusGroups(allCustomizationSteps as any[])

	const isFirstStep = currentStep === 0
	const isLastStep = currentStep >= customizationSteps.length - 1

	const goToNextStep = () => {
		const newStep = currentStep + 1
		if (newStep < customizationSteps.length) {
			setStep(newStep)
		}
	}

	const goToPreviousStep = () => {
		const newStep = currentStep - 1
		if (newStep >= 0) {
			setStep(newStep)
		}
	}

	return (
		<div className="controlBar bg-bbx-glow fixed bottom-0 w-full flex justify-between items-center py-[8px] px-[10px] md:py-[16px] md:px-[30px] text-white z-40">
			<div className="flex-grow flex justify-start z-50">
				<button
					type="button"
					className={`${styles.closeBtn} button p-[0px] min-w-fit`}
					onClick={closeModal}
				>
					Exit
				</button>
			</div>
			{!isLastStep && (
				<div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[18px] max-md:text-[16px]">
					<p className="hidden md:block">
						<strong>{currentSelection?.selections.length}</strong> of {currentSelection?.maxItems}{' '}
						selected
					</p>
					<p className="md:hidden font-bold">
						<strong>{currentSelection?.selections.length}</strong> of {currentSelection?.maxItems}
					</p>
				</div>
			)}
			<div className="flex-grow flex justify-end gap-[16px]">
				{!isFirstStep && !isLastStep && (
					<div
						className={`${
							customizationType === 'RMC' &&
							currentSelection?.selections.length !== currentSelection?.maxItems
								? styles.nextBtn
								: ''
						} relative`}
					>
						<button
							type="button"
							className={`button button-secondary border-white ${
								customizationType === 'RMC' &&
								currentSelection?.selections.length !== currentSelection?.maxItems
									? 'disabled'
									: ''
							} py-[6px] md:py-[10px] px-[10px] md:px-[16px] min-w-fit`}
							onClick={goToPreviousStep}
						>
							<svg
								fill="#ffffff"
								height="16"
								viewBox="0 -960 960 960"
								width="16"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="m384-109.85-370.15-370.15 370.15-370.15 53.77 53.77-316.39 316.38 316.39 316.38z"
									transform="translate(192.5, 0)"
								/>
							</svg>
							<span className="hidden md:block">Previous</span>
						</button>
						{/* Notification message: no selection made */}
						{showErrorMessage && (
							<p className="hidden">
								{currentSelection?.maxItems === 1
									? 'You must select 1 item'
									: `You must select ${currentSelection?.maxItems} items`}
							</p>
						)}
					</div>
				)}
				{!isLastStep && (
					<div
						className={`${
							currentSelection?.selections.length !== currentSelection?.maxItems
								? styles.nextBtn
								: ''
						} relative`}
					>
						<button
							type="button"
							className={`button button-action light ${
								currentSelection?.selections.length !== currentSelection?.maxItems ? 'disabled' : ''
							} py-[8px] md:py-[10px] px-[10px] md:px-[16px] min-w-fit`}
							onClick={goToNextStep}
						>
							<span className="hidden md:block">Next</span>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								height="16px"
								viewBox="0 -960 960 960"
								width="16px"
								fill="#ffffff"
							>
								<path d="m287.62-109.85-53.77-53.77L550.23-480 233.85-796.38l53.77-53.77L657.77-480 287.62-109.85Z" />
							</svg>
						</button>
						{/* Notification message: no selection made */}
						{showErrorMessage && (
							<p className="hidden">
								{currentSelection?.maxItems === 1
									? 'You must select 1 item'
									: `You must select ${currentSelection?.maxItems} items`}
							</p>
						)}
					</div>
				)}
				{isLastStep && (
					<button
						type="button"
						className="button button-action light  py-[6px] md:py-[10px]"
						onClick={submitSelections}
					>
						<span>Confirm & Submit</span>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							height="16px"
							viewBox="0 -960 960 960"
							width="16px"
							fill="#ffffff"
						>
							<path d="m287.62-109.85-53.77-53.77L550.23-480 233.85-796.38l53.77-53.77L657.77-480 287.62-109.85Z" />
						</svg>
					</button>
				)}
			</div>
		</div>
	)
}
